/*
  industrialkonzept Next: code field in the cart (snippets/ik-cart-code.liquid, 2026-10-09).
  Applies the code to the cart, then lets the theme redraw items and totals (theme:update:cart,
  assets/theme-events.js). A code that does not apply is taken off the cart again right away.
  Listens on the document, so it keeps working after the theme redraws the cart; in the capture
  phase, because the drawer stops clicks from bubbling up (@click.stop in snippets/right-drawer.liquid).
  Also: soft shadow and the hint "↓ 2 more items" on the bar with total and checkout while items are
  hidden under it (snippets/ik-cart-more.liquid); tapping the hint scrolls to the next hidden item.
  And: nothing from outside (cookie bar, newsletter flyout) may cover checkout; the bar moves up above it.
*/
(() => {
  const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';

  const send = async (code) => {
    const r = await fetch(root + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ discount: code })
    });
    if (!r.ok) throw new Error('cart ' + r.status);
    return r.json();
  };
  const redraw = () => document.dispatchEvent(new CustomEvent('theme:update:cart'));
  const say = (box, text) => {
    const msg = box.querySelector('[data-ikn-code-msg]');
    if (msg) msg.textContent = text || '';
  };
  // the field sits right above the bar with total and checkout, which stays at the bottom on phones
  // (assets/ik-cart.css): scroll it up far enough that the bar does not cover it. The room the bar takes is
  // measured on screen, because the bar may stand higher than its own height (lifted above a cookie bar,
  // --ikn-cover); a second look after the scroll (and after the bar settled) moves on if still covered
  const reveal = (box) => {
    const drawer = box.closest('[data-cart-drawer]');
    const bar = drawer ? drawer.querySelector(':scope > .mt-auto') : document.querySelector('.ikn-cart-foot');
    const scroller = drawer ? drawer.closest('#right-drawer-slot') : null;
    const covers = bar && getComputedStyle(bar).position === 'sticky';
    const bottom = scroller ? scroller.getBoundingClientRect().bottom : window.innerHeight;
    box.style.scrollMarginBottom = covers
      ? Math.max(0, Math.round(bottom - bar.getBoundingClientRect().top)) + 8 + 'px'
      : '';
    box.scrollIntoView({ block: 'end', behavior: 'smooth' });
    if (!covers) return;
    window.setTimeout(() => {
      const gap = box.getBoundingClientRect().bottom - bar.getBoundingClientRect().top + 8;
      // without animation: this must land even if the smooth scroll was cut short
      if (gap > 1) (scroller || window).scrollBy({ top: gap, behavior: 'auto' });
    }, 450);
  };

  document.addEventListener('click', (e) => {
    const open = e.target.closest('[data-ikn-code-open]');
    if (open) {
      const form = open.closest('[data-ikn-code]').querySelector('[data-ikn-code-form]');
      open.hidden = true;
      open.setAttribute('aria-expanded', 'true');
      form.hidden = false;
      form.querySelector('input').focus({ preventScroll: true });
      reveal(form.closest('[data-ikn-code]'));
      return;
    }
    const remove = e.target.closest('[data-ikn-code-remove]');
    if (remove && !remove.disabled) {
      const box = remove.closest('[data-ikn-code]');
      remove.disabled = true;
      send('').then(redraw).catch(() => {
        remove.disabled = false;
        say(box, box.dataset.msgError);
      });
    }
  }, true);

  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('[data-ikn-code-form]');
    if (!form) return;
    e.preventDefault();
    const box = form.closest('[data-ikn-code]');
    const input = form.querySelector('input');
    const btn = form.querySelector('button');
    const code = input.value.trim();
    if (!code || btn.disabled) return;
    btn.disabled = true;
    say(box, '');
    try {
      const cart = await send(code);
      const hit = (cart.discount_codes || []).find((d) => d.code.toLowerCase() === code.toLowerCase());
      if (hit && hit.applicable) {
        redraw();
        return;
      }
      await send(''); // never leave a code on the cart that does not apply
      say(box, box.dataset.msgInvalid);
      input.select();
    } catch (err) {
      say(box, box.dataset.msgError);
    }
    reveal(box);
    btn.disabled = false;
  }, true);

  // the bar with total and checkout stays at the bottom (assets/ik-cart.css). While items are hidden
  // under it, it gets a soft shadow and says how many ("↓ 2 more items"), so nobody takes the bar for the
  // end of the list; at the end of the list both go away
  const BARS = '[data-cart-drawer] > .mt-auto, [data-ikn-template=cart] .ikn-cart-foot';
  const itemsFor = (bar) => {
    const drawer = bar.closest('[data-cart-drawer]');
    return (drawer || document.getElementById('MainContent') || document).querySelectorAll('li[id^=CartItem]');
  };
  // an item counts as hidden while less than 60 % of it is above the bar
  const hiddenItems = (bar) => {
    const top = bar.getBoundingClientRect().top;
    return [...itemsFor(bar)].filter((li) => {
      const r = li.getBoundingClientRect();
      return r.height > 0 && top - r.top < r.height * 0.6;
    });
  };
  // something from outside at the bottom of the screen (cookie bar, newsletter flyout, Shopify preview
  // bar) would cover checkout: how many pixels it takes from the bottom. Found by looking at what is
  // really on top at the bottom edge below the bar, also inside shadow DOM (the cookie bar lives there).
  // Big windows (over 60 % of the screen) are left alone: the customer has to close those first anyway
  const fixedTop = (el) => {
    for (let e = el; e && e !== document.body && e !== document.documentElement;
      e = e.parentElement || (e.getRootNode && e.getRootNode().host)) {
      if (getComputedStyle(e).position === 'fixed') return e.getBoundingClientRect().top;
    }
    return null;
  };
  const coverFor = (bar) => {
    const r = bar.getBoundingClientRect();
    const x = r.left + r.width / 2;
    const y = window.innerHeight - 2;
    let el = document.elementFromPoint(x, y);
    while (el && el.shadowRoot) {
      const inner = el.shadowRoot.elementFromPoint(x, y);
      if (!inner || inner === el) break;
      el = inner;
    }
    if (!el || bar.contains(el) || (el.closest && el.closest('#right-drawer-slot, #MainContent'))) return 0;
    const top = fixedTop(el);
    const cover = top === null ? 0 : Math.round(window.innerHeight - top);
    return cover > 0 && cover < window.innerHeight * 0.6 ? cover : 0;
  };
  // drawer: set on the drawer (also pads the end of the list, so the last item can come out from under
  // the bar); cart page: on the bar itself (assets/ik-cart.css, --ikn-cover)
  const lift = (bar, sticky) => {
    const host = bar.closest('[data-cart-drawer]') || bar;
    const now = parseInt(host.style.getPropertyValue('--ikn-cover'), 10) || 0;
    const cover = sticky ? coverFor(bar) : 0;
    if (cover === now) return false;
    if (cover) host.style.setProperty('--ikn-cover', cover + 'px');
    else host.style.removeProperty('--ikn-cover');
    return true;
  };
  const markBar = (bar) => {
    const above = bar.previousElementSibling;
    // a closed drawer is not on screen: nothing to count or lift there
    const sticky = bar.getClientRects().length > 0 && getComputedStyle(bar).position === 'sticky';
    const lifted = lift(bar, sticky);
    const more = sticky && !!above && above.getBoundingClientRect().bottom > bar.getBoundingClientRect().top + 1;
    bar.classList.toggle('ikn-cartbar--more', more);
    const hint = bar.querySelector('[data-ikn-cartmore]');
    if (!hint) return lifted;
    const n = more ? hiddenItems(bar).length : 0;
    if (n > 0) {
      const text = n === 1 ? hint.dataset.one : hint.dataset.many.replace('[n]', n);
      const label = hint.querySelector('[data-ikn-cartmore-text]');
      if (label.textContent !== text) label.textContent = text;
    }
    const changed = hint.hidden !== (n === 0);
    if (changed) hint.hidden = n === 0;
    return changed || lifted;
  };
  let queued = false;
  const mark = () => {
    queued = false;
    // showing or hiding the hint changes the height of the bar: count once more with the new height
    document.querySelectorAll(BARS).forEach((bar) => {
      if (markBar(bar)) markBar(bar);
    });
  };
  // next frame; if no frame comes (page in the background), a timer does it
  const run = () => {
    if (queued) mark();
  };
  const soon = () => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(run);
    window.setTimeout(run, 120);
  };
  // the drawer slides in for 300 ms and the theme redraws after its request: count again a little later
  const later = () => {
    soon();
    window.setTimeout(soon, 350);
    window.setTimeout(soon, 800);
  };

  // tap on the hint: scroll until the next hidden item is in full view above the bar
  document.addEventListener('click', (e) => {
    const hint = e.target.closest('[data-ikn-cartmore]');
    if (!hint) return;
    const bar = hint.closest('[data-cart-drawer] > .mt-auto, .ikn-cart-foot');
    const next = bar && hiddenItems(bar)[0];
    if (!next) return;
    const by = next.getBoundingClientRect().bottom - bar.getBoundingClientRect().top + 8;
    (bar.closest('#right-drawer-slot') || window).scrollBy({ top: by, behavior: 'smooth' });
  }, true);

  // when to count again: scrolling (page and drawer), size changes, the drawer opening, the theme
  // redrawing the items (quantity, remove, add) and pictures arriving.
  // Lists are watched for changes and for their size (the drawer's list grows from 0 when it opens);
  // kept in a WeakSet, not as an attribute: the theme's redraw would wipe an attribute
  const watched = new WeakSet();
  const sizes = window.ResizeObserver ? new ResizeObserver(soon) : null;
  const watch = () => document.querySelectorAll('[data-cart-items]').forEach((list) => {
    if (watched.has(list)) return;
    watched.add(list);
    new MutationObserver(later).observe(list, { childList: true, subtree: true });
    if (sizes) sizes.observe(list);
  });
  const again = () => {
    watch();
    later();
  };
  document.addEventListener('scroll', soon, { capture: true, passive: true });
  window.addEventListener('resize', soon);
  if (window.visualViewport) window.visualViewport.addEventListener('resize', soon);
  document.addEventListener('load', (e) => {
    if (e.target.tagName === 'IMG' && e.target.closest('[data-cart-items]')) soon();
  }, true);
  // the theme sends some of these on the document without bubbling, others on the body or the window
  const events = ['theme:cart-drawer:open', 'baseline:cart:update', 'baseline:cart:afteradditem', 'baseline:cart:cartqtychange'];
  [document, window].forEach((target) => events.forEach((name) => target.addEventListener(name, again)));
  // and as a safety net: any tap can open the drawer or change the cart (cart icon, add to cart, quantity)
  document.addEventListener('click', again, true);
  // windows from outside come and go: added to the page (newsletter) or redrawn in their own shadow DOM
  // (cookie bar, pandectes-cmp)
  const outside = new MutationObserver(later);
  outside.observe(document.body, { childList: true });
  const watchCookieBar = () => {
    const cmp = document.querySelector('pandectes-cmp');
    if (cmp && cmp.shadowRoot && !watched.has(cmp)) {
      watched.add(cmp);
      outside.observe(cmp.shadowRoot, { childList: true, subtree: true, attributes: true });
    }
  };
  watchCookieBar();
  window.addEventListener('load', () => {
    watchCookieBar();
    later();
  });
  again();
})();
